import {
  ActionSearchTemplateInput,
  ActionTemplatesLinkedToThisStake_ActionFragment,
  ActionTemplatesLinkedToThisStake_StakeFragment,
  ActionTemplateSource,
  AddActionTemplateToFirstCatalyst_ActionFragment,
  FeatureEnum,
  PermissionEnum,
  useActionTemplatesLinkedToThisStake_SearchActionLazyQuery,
  useAddActionTemplateToFirstCatalyst_ActionCreateMutation,
  useStakeActionsDetailQuery,
} from '@/graphql/generated';
import { useCallback, useState } from 'react';
import { ActionCard } from '../../publications/roadmap/ActionCard';
import { MessageBox, MessageBoxType } from '@/components/layout/MessageBox';
import { useModal } from '@/components/modals/Modal';
import { useProjectContext } from '@/providers/ProjectContextProvider';
import { LoaderFullscreen } from '@/components/layout/Loader';
import { ActionIllustrationIcon } from '../../publications/roadmap/ActionIllustrationIcon';
import { StringDropdownItem } from '@/components/generic/dropdown/StringDropdown.types';
import { useToast } from '@/components/layout/Toast';
import { Loader } from '@/components/generic/Loader';
import { getLatestCompanyRoadmapSummary } from '@/services/CompanyService';
import { useCurrentUser } from '@/providers/CurrentUserProvider';
import {
  CreateRoadmap,
  CreateRoadmapButton,
} from '../../project/roadmap/CreateRoadmap';
import { generatePath, NavLink } from 'react-router';
import { ArrowRightIcon, PlusIcon, SearchIcon } from '@/components/icons';
import { AppRoutes } from '@/screens/AppRoutes';
import StringDropdown from '../../generic/dropdown/StringDropdown';
import { usePermissionChecker } from '@/hooks/usePermissionChecker';
import { useTranslation } from '@/hooks/useTranslation';

export function StakeActionsDetail({ stakeId }: { stakeId: string }) {
  const { t } = useTranslation();
  const { isGranted } = usePermissionChecker();
  const readOnly = isGranted(
    FeatureEnum.RoadmapManagement,
    PermissionEnum.Read,
    true,
  );
  const modal = useModal();
  const projectContext = useProjectContext();

  const query = useStakeActionsDetailQuery({
    variables: {
      id: stakeId,
    },
    fetchPolicy: 'no-cache',
  });

  if (query.loading) {
    return <LoaderFullscreen />;
  }

  if (query.error) {
    return (
      <MessageBox type={MessageBoxType.Error}>{query.error.message}</MessageBox>
    );
  }

  if (!query.data?.getStakeWithComputedFields) {
    return null;
  }

  const stake = query.data?.getStakeWithComputedFields;

  const roadmapSummary = getLatestCompanyRoadmapSummary(
    projectContext?.enterprise,
  );

  if (!roadmapSummary) {
    if (readOnly) {
      return <CreateRoadmap readOnly={readOnly} />;
    }
    // No roadmap -> we need to create one first
    return (
      <MessageBox type={MessageBoxType.Info}>
        <div className="flex items-center gap-2 justify-between w-full">
          <div>{t('stake.detailModal.actions.no_roadmap')}</div>
          <CreateRoadmapButton />
        </div>
      </MessageBox>
    );
  }

  const actions = stake.actions?.filter((action) => !action.isTemplate) || [];

  return (
    <div className="p-4 space-y-4">
      <div className="space-y-2">
        <div className="flex items-center gap-4 justify-between">
          <h6>{t('stake.detailModal.actions.title')}</h6>
          <NavLink
            to={generatePath(AppRoutes.Roadmap)}
            onClick={() => modal.closeModal()}
          >
            <button className="tertiary small bg-transparent">
              <div>{t('stake.detailModal.actions.see_roadmap')}</div>
              <ArrowRightIcon />
            </button>
          </NavLink>
        </div>
        {actions.length > 0 ? (
          <div className="py-4">
            <div className="flex flex-col gap-2">
              {actions
                .filter((action) => !action.isTemplate)
                .map((action) => (
                  <div onClick={() => modal.closeModal()}>
                    <ActionCard action={action} key={action.id} />
                  </div>
                ))}
            </div>
          </div>
        ) : (
          <MessageBox type={MessageBoxType.Info}>
            {t('stake.detailModal.actions.no_action')}
          </MessageBox>
        )}
      </div>
      <ActionTemplatesLinkedToThisStake stake={stake} />
    </div>
  );
}

function ActionTemplatesLinkedToThisStake({
  stake,
}: {
  stake: ActionTemplatesLinkedToThisStake_StakeFragment;
}) {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();

  const [hasSearchBeenTriggered, setHasSearchBeenTriggered] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResult, setSearchResult] = useState<
    ActionTemplatesLinkedToThisStake_ActionFragment[]
  >([]);
  const [source, setSource] = useState<ActionTemplateSource>(
    ActionTemplateSource.Goodsteps,
  );

  const [searchActionTemplatesLazyQuery] =
    useActionTemplatesLinkedToThisStake_SearchActionLazyQuery();
  const refreshActionsList = useCallback(
    (searchCriteria: ActionSearchTemplateInput) => {
      setIsSearching(true);
      searchActionTemplatesLazyQuery({
        variables: {
          input: searchCriteria,
        },
      })
        .then((result) => {
          result.data?.searchActionTemplates &&
            setSearchResult(result.data.searchActionTemplates);
        })
        .catch((err) => {
          console.error(err.message);
        })
        .finally(() => setIsSearching(false));
    },
    [searchActionTemplatesLazyQuery],
  );

  const sourceItems: StringDropdownItem[] = [
    {
      id: ActionTemplateSource.Goodsteps,
      label: 'Goodsteps',
    },
    {
      id: ActionTemplateSource.OwnTemplates,
      label: t('stake.detailModal.actions.templates.my_templates'),
    },
  ];
  const sourceItem = sourceItems.find((item) => item.id === source) || null;
  const setSourceItem = (item: StringDropdownItem | null) => {
    if (item) {
      setSource(item.id as ActionTemplateSource);
      search(item.id as ActionTemplateSource);
    }
  };

  const search = (source: ActionTemplateSource) => {
    if (stake.origin?.id) {
      refreshActionsList({
        companyId: currentUser?.company?.id || '',
        source: source,
        stake: {
          id: stake.origin.id,
        },
      });
    } else {
      setSearchResult([]);
    }
  };

  if (isSearching) {
    return <LoaderFullscreen />;
  }

  if (!hasSearchBeenTriggered) {
    return (
      <div className="flex items-center justify-center">
        <button
          className="primary small"
          onClick={() => {
            setHasSearchBeenTriggered(true);
            search(ActionTemplateSource.Goodsteps);
          }}
        >
          <SearchIcon />
          <div>{t('stake.detailModal.actions.templates.find')}</div>
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h6>{t('stake.detailModal.actions.templates.title')}</h6>
        <div className="flex items-center gap-2">
          <label className="sami-body-small-bold">
            {t('stake.detailModal.actions.templates.search_into')}
          </label>
          <StringDropdown
            availableItems={sourceItems}
            item={sourceItem}
            setItem={setSourceItem}
          />
        </div>
      </div>
      <div>
        {searchResult.length > 0 ? (
          <div className="flex flex-col gap-2">
            {searchResult.map((action) => (
              <div className="flex flex-col items-center gap-4 w-full rounded-xl bg-white border-2 border-transparent">
                <div className="flex flex-col items-center w-full grow">
                  <div className="flex items-center gap-2 w-full grow p-1">
                    <ActionIllustrationIcon action={action} />
                    <div className="sami-body-small-bold grow">
                      {action.title}
                    </div>
                    <AddActionTemplateToFirstCatalyst actionTemplate={action} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <MessageBox type={MessageBoxType.Info}>
            {t('stake.detailModal.actions.templates.no_template')}
          </MessageBox>
        )}
      </div>
    </div>
  );
}

function AddActionTemplateToFirstCatalyst({
  actionTemplate,
}: {
  actionTemplate: AddActionTemplateToFirstCatalyst_ActionFragment;
}) {
  const { t } = useTranslation();
  const toast = useToast();

  // Fetch first catalyst
  const projectContext = useProjectContext();
  const roadmapSummary = getLatestCompanyRoadmapSummary(
    projectContext?.enterprise,
  );
  const catalyst = roadmapSummary?.catalysts[0];

  const [addAction] =
    useAddActionTemplateToFirstCatalyst_ActionCreateMutation();
  const [isAdding, setIsAdding] = useState(false);
  const add = () => {
    setIsAdding(true);
    addAction({
      variables: {
        input: {
          actionTemplateId: actionTemplate.id,
          roadmapId: roadmapSummary?.id || '',
          catalystId: catalyst?.id || '',
          companyId: projectContext?.enterprise?.id || '',
        },
      },
    })
      .then(() => {
        toast.openToastWithMessage(
          t('toast:stake.detail_modal.action.add.success'),
        );
      })
      .catch((err) => console.error(err))
      .finally(() => setIsAdding(false));
  };

  return (
    <button className="small tertiary" onClick={add} disabled={isAdding}>
      {isAdding ? <Loader /> : <PlusIcon />}
      <div>{t('global:add')}</div>
    </button>
  );
}
